import React from 'react'

import Popover from '../../gd-components/gd-popover/popover-v2'
import PopoverPosition from '../../gd-components/gd-popover/popover-position'
import {PopoverAttachmentType} from '../../gd-components/gd-popover/@const'
import VScroll from '../../gd-components/scrolling/custom-v-scroll/v-scroll'
import HintPopover from '../hint-popover/hint-popover'

import _ from 'lodash'
import Classes from 'classnames'
import { Deferred } from 'simply-deferred'

type Props = {
    options                 : any[]                                        // all available options
    values?                 : any[]                                        // checked values
    renderValue             : 'form' | 'func'

    renderValueFunc?        : func
    renderOptionFunc?       : func

    placeholder?            : string
    disabled?               : boolean
    multiselect?            : boolean
    className?               : string

    onTogglePopover?        : callback
    onChange?                : callback                                    // every time value is changed, including react events
    onUserSelect?            : callback                                    // when user select new value

    form?                    : any
    name?                    : string

    canHaveNoValue?          : boolean
    noValueLabel?            : string
    noValueValue?            : any

    popoverFitTargetWidth?  : boolean                                      // if true popover width matches selector (value) width
    popoverStyle?            : any                                          // special styles to popover

    attachmentType?          : PopoverAttachmentType | 'full'| 'vertical'| 'horizontal'
    attachment?              : string
    targetAttachment?        : string
    targetOffset?            : string
    popoverClass?            : string                                      // if passed will add this class to popover

    searchable?              : boolean
    keyboardNavigation?      : boolean
    hint?                    : string
}

type State = {
    showPopover      : boolean
    values           : any[]
    searchString     : string | null
    currentCursorKey : number | null
}

// Multi select for custom options list, base on src/core/gd-ui/select-user/select-user.jsx
export default class SelectMultiGeneric extends React.Component<Props, State> {

    static defaultProps: Partial<Props> = {
        attachmentType    : "full",
        attachment        : "top center",
        targetAttachment  : "bottom center",
        targetOffset      : "0 0",
        searchable        : true,
        multiselect       : true,
        keyboardNavigation: true,
    }

    private searchInput: any

    constructor(props:Props) {
        super(props)

        const values = props.form ? props.form.getValue(props.name) : props.values

        this.state = {
            showPopover: false,
            values: values,
            searchString: null,
            currentCursorKey: null,
        }
    }

    componentDidMount() {
        if (this.props.form) this.props.form.registerFormComponent(this.props.name, this)
    }

    UNSAFE_componentWillReceiveProps(nextProps:Props) {
        if (nextProps.form) {
            const formValue = nextProps.form.getValue(nextProps.name)
            if (formValue !== this.state.values) {
                this.setState({values: formValue})
            }
        } else {
            if (nextProps.values && nextProps.values !== this.state.values) {
                this.setState({values: nextProps.values})
            }
        }
    }

    UNSAFE_componentWillUpdate(nextProps:Props, nextState:State) {
        if (nextState.values !== this.state.values) {
            if (this.props.onChange) this.props.onChange(nextState.values)
        }
    }

    componentDidUpdate() {
        if (this.searchInput) this.searchInput.focus()
    }

    togglePopover = () => {
        if (this.props.disabled) return

        const showPopover = !this.state.showPopover
        this.setState({
            showPopover: showPopover
        }, ()=>{
            if (this.props.onTogglePopover) this.props.onTogglePopover(showPopover)
        })
    }

    hidePopover = (selectedOption:any = null) => {
        let searchString:string | null = null
        let showPopoper = false
        let currentCursorKey:number | null = null
        let values = this.state.values
        const stateChanged = new Deferred()

        if (selectedOption) {
            switch (selectedOption.type) {
                case 'item':
                    showPopoper = !!this.props.multiselect
                    const itemValue = selectedOption.item.value
                    if (this.props.multiselect) {
                        const idx = values.indexOf(itemValue)
                        if (idx === -1) values.push(itemValue)
                        else values.splice(idx, 1)
                    } else {
                        if (gd.utils.checkIsArray(itemValue)) values = itemValue
                        else values = [itemValue]
                    }

                    searchString = this.state.searchString

                    currentCursorKey = this.state.currentCursorKey
                    break

                case 'no-value':
                    values = []
                    break
            }
        }

        this.setState({
            searchString: searchString,
            showPopover: showPopoper,
            currentCursorKey: currentCursorKey,
            values: values,
        },()=>{
            stateChanged.resolve()
            if (this.props.onTogglePopover) this.props.onTogglePopover(false)
        })

        return stateChanged
    }

    onOptionSelected(option:any) {
        const stateChanged = this.hidePopover(option)

        if (!this.props.form && !this.props.onUserSelect) return

        switch (option.type) {
            case 'item':
                stateChanged.done(() => {
                    this.props.form && this.props.form.onFormComponentValueChange(this.props.name, this.state.values)
                    this.props.onUserSelect && this.props.onUserSelect(this.state.values)
                })
                break

            case 'no-value':
                this.props.form && this.props.form.onFormComponentValueChange(this.props.name, this.props.noValueValue)
                break
        }
    }

    onSearchChange = (event:any) => {
        const newValue = event.target.value
        this.setState({
            searchString: newValue.length > 0 ? newValue : null
        })
    }

    onPopoverKeyDown = (event:any) => {
        if (!this.props.keyboardNavigation) return

        const selectOptions = this._getOptions()

        let newValue

        switch(event.key){
            case 'Escape': this.hidePopover(); break

            case 'ArrowUp':
                if (this.state.currentCursorKey === null) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey - 1
                    if (newValue < 0) newValue = 0
                }

                this.setState({currentCursorKey: newValue})
                break

            case 'ArrowDown':
                if (this.state.currentCursorKey === null) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey + 1
                    if (newValue > selectOptions.length - 1) newValue = selectOptions.length - 1
                }

                this.setState({currentCursorKey: newValue})
                break

            case 'Enter':
                if (this.state.currentCursorKey !== null) {
                    const curOption = selectOptions[this.state.currentCursorKey]
                    if (curOption) this.onOptionSelected(curOption)
                }
                break
        }
    }

    _getOptions() {

        // 2 types of options: no-value, item

        const selectOptions:any[] = []

        let searchString = this.state.searchString
        if (searchString) searchString = searchString.toLowerCase()

        if (this.props.canHaveNoValue) {
            // make sure filter applies (search)
            if (!searchString || this.props.noValueLabel.toLowerCase().includes(searchString)) {
                selectOptions.push({
                    type: 'no-value'
                })
            }
        }

        if (this.props.options && this.props.options.length > 0) {

            let options = this.props.options.slice()

            if (searchString) {
                options = _.filter(options, opt=>{
                    return opt.label.toLowerCase().includes(searchString)
                })
            }

            options.forEach(opt => {
                selectOptions.push({
                    type: 'item',
                    item: opt
                })
            })
        }

        return selectOptions
    }

    renderPopover() {
        if (!this.state.showPopover) return

        let content:any = []
        const selectOptions = this._getOptions()

        if (selectOptions.length>0) {
            selectOptions.map((option,key)=>{
                const classes = Classes('option',{
                    'cursor-highlighted': key === this.state.currentCursorKey
                })

                switch (this.props.renderValue) {
                    case 'form':
                        switch (option.type) {
                            case 'no-value':
                                content.push(
                                    <div key={key} className={classes} onClick={this.onOptionSelected.bind(this,option)}>
                                        <div className="label">{this.props.noValueLabel}</div>
                                    </div>
                                )
                                break

                            case 'item':
                                const isSelected = _.includes(this.state.values, option.item.value)
                                content.push(
                                    <div key={key} className={classes} onClick={this.onOptionSelected.bind(this,option)}>
                                        {this.props.multiselect && <div className={Classes("ui5-cb", "s-13", {selected:isSelected})}/>}
                                        {option.item.icon && <div className={Classes("ico", option.item.icon)}/>}
                                        <div className="label">{option.item.label}</div>
                                    </div>
                                )
                                break
                        }
                        break

                    case 'func':
                        content.push(this.props.renderOptionFunc(option.item, this.state.values, this.onOptionSelected.bind(this,option)))
                        break
                }
            })
        } else {
            content = <div className="option empty">No items found</div>
        }

        let searchRes
        if (this.props.searchable && (selectOptions.length>=5 || this.state.searchString)) {
            searchRes = (
                <div className="search">
                    <input ref={r=>this.searchInput = r}
                           className="search-input gd-input small"
                           placeholder="Search"
                           value={this.state.searchString ? this.state.searchString : ""}
                           onChange={this.onSearchChange}/>
                </div>
            )
        } else {
            // still need to autofocus so keyboard work
            searchRes = (
                <span ref={r=>this.searchInput = r} className="hidden-search-input" tabIndex={0} />
            )
        }

        const popoverClasses = Classes('gd-select-user-popover', 'gd-select-multi-generic-popover', this.props.popoverClass, 'render-'+this.props.renderValue)

        const popoverPosition = new PopoverPosition({
            attachmentType  : this.props.attachmentType as PopoverAttachmentType,
            attachment      : this.props.attachment,
            targetAttachment: this.props.targetAttachment,
            targetOffset    : this.props.targetOffset,
        })

        return (
            <Popover
                popoverClass    = {popoverClasses}
                className       = "dropdown-checkboxes-container"
                targetClass     = "pop-target"
                position        = {popoverPosition}
                onKeyDown       = {this.onPopoverKeyDown}
                onClickOutside  = {()=>{this.hidePopover()}}
                fitTargetWidth  = {this.props.popoverFitTargetWidth}
                style           = {this.props.popoverStyle}
            >
                {searchRes}

                <VScroll>
                    <div className="ui5-popover-menu">
                        {content}
                    </div>
                </VScroll>
            </Popover>
        )

    }

    renderValue() {
        const counter = this.state.values && this.state.values.length ? this.state.values.length : 0
        let res

        switch (this.props.renderValue) {
            case 'form':
                if (counter > 0) {
                    const selectedItems = this.state.values.map(itemValue => {
                        const opt:any = _.find(this.props.options, {value: itemValue})
                        if (opt) {
                            return (
                                <div key        = {opt.value}
                                     className  = "item">
                                    {opt.icon && <div className={Classes("ui5-ico-13 m-r-5", opt.icon)}/>}
                                    {opt.label}
                                </div>
                            )
                        } else {
                            console.error('no user found')
                            return false
                        }
                    })

                    res = (
                        <div className="element-content has-value">
                            {selectedItems}
                            <div key="arrow" className="arrow-icon i5-big-angle-down" />
                        </div>
                    )
                } else {
                    res = (
                        <div className="element-content no-value">
                            <span key="label">{this.props.placeholder}</span>
                            <div key="arrow" className="arrow-icon i5-big-angle-down" />
                        </div>
                    )
                }
                break

            case 'func':
                res = this.props.renderValueFunc(_.filter(this.props.options, o=>this.state.values.includes(o.value)))
                break
        }

        return (
            <div className="value-container pop-target" onClick={this.togglePopover}>
                {res}
            </div>
        )

    }

    render() {
        const classes = Classes('gd-select-user gd-select-multi-generic multi-select', this.props.className, {
            'render-form': ['form','func'].includes(this.props.renderValue),
            'is-disabled': this.props.disabled,
            'open': this.state.showPopover
        })

        return (
            <div className={classes}>
                {this.renderValue()}
                {this.props.hint && !this.state.showPopover && <HintPopover parent={this} hint={this.props.hint}/>}
                {this.renderPopover()}
            </div>
        )
    }
}
